import { Component, OnInit } from "@angular/core";
import { input, output } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { UserService } from "@modules/user-management/_services/user.service";
import { EmployeeError } from "@shared/models/file-manager";
import { PaymentOrderHistoryLog, PaymentOrderHistoryLogItem } from "@shared/models/payment-order-history-log.interface";
import { FileManagementService } from "@shared/services/file-management/file-management.service";
import { catchError, debounceTime, distinctUntilChanged, map, of } from "rxjs";

@Component({
	selector: "app-file-history-log",
	templateUrl: "./file-history-log.component.html",
	styleUrl: "./file-history-log.component.scss"
})
export class FileHistoryLogComponent implements OnInit {
	datasource = input<PaymentOrderHistoryLog>();
	closeLog = output<void>();

	searchForm!: FormGroup;
	isPayrollIntegration = false;

	employeeErrors: EmployeeError[] = [];
	historyItems: PaymentOrderHistoryLogItem[] = [];
	filteredItems: PaymentOrderHistoryLogItem[] = [];
	taskCompletedAuthor = "";
	searchTerm = "";

	constructor(
		private fileManagementService: FileManagementService,
		private userService: UserService,
		private formBuilder: FormBuilder
	) {}

	ngOnInit(): void {
		this.isPayrollIntegration = this.datasource()?.errorType?.toLowerCase() === "payroll integration";
		this.getTaskCompletedAuthor();
		if (this.isPayrollIntegration) {
			this.buildHistoryLogItems(this.datasource());
			this.applyFilter();
		} else {
			this.getEmployeeErrors();
		}
		this.initForm();
	}

	initForm(): void {
		this.searchForm = this.formBuilder.group({
			search: [""]
		});

		this.searchForm
			.get("search")
			?.valueChanges.pipe(debounceTime(400), distinctUntilChanged())
			.subscribe(searchValue => {
				this.searchTerm = (searchValue ?? "").trim().toLowerCase();
				this.applyFilter();
			});
	}

	getSubheader(): string {
		const ds = this.datasource();
		if (this.isPayrollIntegration) return "Payroll Integration";
		else return `${this.datasource()?.fileName} - ${this.datasource()?.errorType} File`;
	}

	getEmployeeErrors(): void {
		const taskId = this.datasource()?.taskId ?? "";
		if (taskId === "") return;

		this.fileManagementService.getFileByTaskId(taskId, -1, 0).subscribe(result => {
			this.employeeErrors = result.employeeErrors;
			this.buildHistoryLogItems(this.datasource());
			this.applyFilter();
		});
	}

	getTaskCompletedAuthor(): void {
		const userId = this.datasource()?.taskOverridingStatus?.overriddenBy?.value;
		if (!userId) return;

		this.userService
			.getUserById(userId)
			.pipe(
				map(userInfo => `${userInfo.firstName ?? ""} ${userInfo.lastName ?? ""}`.trim()),
				catchError(() => of("Unknown User"))
			)
			.subscribe(author => {
				this.taskCompletedAuthor = author;
			});
	}

	buildHistoryLogItems(ds: PaymentOrderHistoryLog | undefined): void {
		if (!ds) return;

		const textOrEmpty = (v: unknown): string => (typeof v === "string" ? v.trim() || "" : ((v ?? "") as string));
		const toDateOrNow = (v: Date | string | number | null | undefined): Date => {
			if (v instanceof Date) return v;
			if (v !== null && v !== undefined) {
				const d = new Date(v);
				if (!Number.isNaN(d.getTime())) return d;
			}
			return new Date();
		};

		//build integration errors
		const integrationErrors = ds?.integrationErrorMessages ?? [];
		integrationErrors.forEach(error => {
			this.historyItems.push({
				createdAt: new Date(error.timeStamp),
				indicator: "ERROR",
				title: [
					{ text: error.fileName.toUpperCase(), style: "highlight" },
					{ text: "has errors", style: "normal" }
				],
				subtitle: error.message
			});
		});

		//build handshake errors
		if (ds?.handShakeResults) {
			const handshakeResults = ds?.handShakeResults?.results ?? [];
			if (handshakeResults.length) {
				handshakeResults.forEach(error => {
					const errorMessage = `${error.type.toLocaleString()} - ${error.message}`;
					this.historyItems.push({
						createdAt: toDateOrNow(ds?.fileProcessedAt),
						indicator: "ERROR",
						title: [
							{ text: textOrEmpty(ds?.errorFileType), style: "highlight" },
							{ text: this.isPayrollIntegration ? "" : "file", style: "normal" },
							{ text: this.isPayrollIntegration ? "" : textOrEmpty(ds?.fileName), style: "highlight" },
							{ text: "has validation errors", style: "normal" }
						],
						subtitle: errorMessage
					});
				});
			} else {
				const handshakeResultsSingle = ds?.handShakeResults?.errorMessage!;
				if (handshakeResultsSingle)
					this.historyItems.push({
						createdAt: toDateOrNow(ds?.fileProcessedAt),
						indicator: "ERROR",
						title: [
							{ text: textOrEmpty(ds?.errorFileType), style: "highlight" },
							{ text: this.isPayrollIntegration ? "" : "file", style: "normal" },
							{ text: this.isPayrollIntegration ? "" : textOrEmpty(ds?.fileName), style: "highlight" },
							{ text: "has validation errors", style: "normal" }
						],
						subtitle: handshakeResultsSingle
					});
			}
		}

		//build employee errors
		this.employeeErrors.forEach(error => {
			let validationErrors: string[] = [];
			error.validationErrors.forEach(error => {
				const field = error.field ? `${error.field} - ` : "";
				validationErrors.push(`${field}${error.message}`);
			});

			this.historyItems.push({
				createdAt: toDateOrNow(ds?.fileCreatedAt),
				indicator: "ERROR",
				title: [
					{ text: textOrEmpty(ds?.errorFileType), style: "highlight" },
					{ text: "file", style: "normal" },
					{ text: textOrEmpty(ds?.fileName), style: "highlight" },
					{ text: "was uploaded with errors.", style: "normal" }
				],
				subtitle: `Employee ${textOrEmpty(error.employee.firstName)} ${textOrEmpty(
					error.employee.lastName
				)} (ID #${textOrEmpty(error.employee.externalId)}) - ${textOrEmpty(
					error.executionError?.message
				)} \n ${validationErrors.join("\n")}`
			});
		});

		//task completion
		const taskCompletion = ds?.taskOverridingStatus;
		if (taskCompletion) {
			this.historyItems.push({
				createdAt: toDateOrNow(taskCompletion?.overriddenAt),
				indicator: "COMPLETED",
				title: [
					{ text: this.taskCompletedAuthor, style: "highlight" },
					{ text: "has marked as", style: "normal" },
					{ text: "Completed", style: "highlight" },
					{ text: "the", style: "normal" },
					{ text: textOrEmpty(ds?.errorType), style: "highlight" },
					{ text: "file upload.", style: "normal" }
				],
				subtitle: ""
			});
		}

		//sort in descending order
		this.historyItems.sort((a, b) => {
			const timeA = a?.createdAt instanceof Date ? a?.createdAt.getTime() : new Date(a.createdAt!).getTime();
			const timeB = b?.createdAt instanceof Date ? b?.createdAt.getTime() : new Date(b.createdAt!).getTime();
			return timeB - timeA;
		});
	}

	applyFilter(): void {
		if (!this.searchTerm) {
			this.filteredItems = this.historyItems;
			return;
		}

		this.filteredItems = this.historyItems.filter(item => {
			const inSubtitle = item?.subtitle?.toLowerCase().includes(this.searchTerm);
			const inTitle = item.title.some(segment => segment.text.toLowerCase().includes(this.searchTerm));
			return inSubtitle || inTitle;
		});
	}
}
